import React, { createContext, useContext, useState, useEffect } from 'react';
import { products } from '../data/mock';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [promoCode, setPromoCode] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(0);

  useEffect(() => {
    const savedCart = localStorage.getItem('physiomobile_cart');
    if (savedCart) {
      setItems(JSON.parse(savedCart));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('physiomobile_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (productId, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.productId === productId);
      if (existing) {
        return prev.map((item) =>
          item.productId === productId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      const product = products.find((p) => p.id === productId);
      return [...prev, { productId, quantity, product }];
    });
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((item) => item.productId !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }
    setItems((prev) =>
      prev.map((item) =>
        item.productId === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    setPromoCode(null);
    setPromoDiscount(0);
    localStorage.removeItem('physiomobile_cart');
  };

  const applyPromoCode = (code) => {
    // Mock promo codes
    const promoCodes = {
      'REHAB10': 10,
      'PHYSIO15': 15,
      'WELCOME20': 20,
    };
    if (promoCodes[code.toUpperCase()]) {
      setPromoCode(code.toUpperCase());
      setPromoDiscount(promoCodes[code.toUpperCase()]);
      return true;
    }
    return false;
  };

  const removePromoCode = () => {
    setPromoCode(null);
    setPromoDiscount(0);
  };

  const subtotal = items.reduce(
    (sum, item) => sum + (item.product?.price || 0) * item.quantity,
    0
  );

  const discountAmount = (subtotal * promoDiscount) / 100;
  const shipping = subtotal >= 75 ? 0 : 5.99;
  const total = subtotal - discountAmount + shipping;

  return (
    <CartContext.Provider
      value={{
        items,
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        subtotal,
        shipping,
        discountAmount,
        promoCode,
        promoDiscount,
        total,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        applyPromoCode,
        removePromoCode,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};